import { HttpService } from 'core/modules/network-service';
import { get, merge } from 'lodash';

// local dependencies
import { getVisibleItems, isVisible } from 'resolvers/getVisibleEntities';
import { SERVICE_RESPONSE_STATUS, CRUD_OPERATION } from 'constants/common';
import { getStore } from 'store';
import { REDUCER } from 'constants/entity-names';

// get store
const store = getStore();

/*************************************************************/

/**
 * @type {class}
 * @desc MemberDetailsPayBalanceSelectCard defines an HTTP service interface to send HTTP network requets.
 * @example
 * import { MemberDetailsPayBalanceSelectCardService } from 'services/memberDetailsPayBalanceSelectCard';
 *
 * // create service instance and send/cancel request
 * const memberDetailsPayBalanceSelectCardService = new MemberDetailsPayBalanceSelectCardService();
 * const result = await memberDetailsPayBalanceSelectCardService.fetchData( '<user-id>' ); // fetch data
 * memberDetailsPayBalanceSelectCardService.cancel(); // cancel an active request
 */
export class MemberDetailsPayBalanceSelectCardService extends HttpService {

    // make request and fetch data
    async fetchComponentData( { url, data } ) {
        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data,
            } );

            // extract data
            const component = get( response, 'body.component', {} );
            const visibility = get( response, 'body.component.visibility', {} );
            const modifyActions = get( response, 'body.component.structure.modifyActions', {} );
            const addAction = get( response, 'body.component.structure.addAction', {} );
            const navActions = get( response, 'body.component.structure.navActions', [] );
    
            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                data: {
                    cards: get( response, 'body.component.data.state.paymentOptions', [] ),
                    noData: get( response, 'body.component.structure.noDataView', {} ),
                    options: get( response, 'body.component.data.options', {} ),
                    addAction: isVisible( addAction, visibility ) ? addAction: {},
                    modifyActions: getVisibleItems( modifyActions, visibility ),
                    navActions: getVisibleItems( navActions, visibility ),
                    widgetConfig: get( response, 'body.component.structure.widgetConfig', {} ),
                    changeConfirmation: get( component, 'structure.widgetConfig.changeConfirmation', {} ),
                    defaultDescription: isVisible( get( component, 'structure.widgetConfig.defaultDescription', {} ), visibility ) ? get( response, 'body.component.structure.widgetConfig.defaultDescription', {} ): {},
                    ...get( response, 'body.component.data.state', {} )
                },
            };
            
        } catch( errResponse ) {
            return this._errorFormatter( errResponse );
        }
    }

     // make request and fetch data
     async setDefaultPaymentInfo( { url, data } ) {

        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data: data,
            } );

            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
            };

        } catch( error ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
            };
        }
    }

    // make request and fetch data
    async deletePaymentInfo( { url, data } ) {

        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data: data,
            } );
    
            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
            };

        } catch( error ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
            };
        }
    }

    // async submitAmountAndPaymentInfo( { url = {}, data = {} } = {} ) {
    //     try {
    //         const response = await this.post( {
    //             host: CONFIG.host,
    //             path: url,
    //             data: data
    //         } );
     
    //         return {
    //             status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
    //             successViewData: {
    //                 ...get( response, 'body.component.data.state', {} ),
    //                 ...get( response, 'body.component.structure', {} ),
    //             },
    //         };

    //     } catch( error ) {
    //         return {
    //             status: SERVICE_RESPONSE_STATUS.FAILURE,
    //             data: null,
    //         };
    //     }
    // }

    // // cancel payment
    // async cancelPayment( { url = {}, data = {} } = {} ) {
    //     try {
    //         const response = await this.post( {
    //             host: CONFIG.host,
    //             path: url,
    //             data: data
    //         } );
     
    //         return {
    //             status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
    //         };

    //     } catch( error ) {
    //         return {
    //             status: SERVICE_RESPONSE_STATUS.FAILURE,
    //             data: null,
    //         };
    //     }
    // }

    // make request and fetch data
    async submitEditedPaymentInfo( { url, data } ) {

        // get ongoing call info from the redux store
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );

        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data: merge( data, {
                    context: {
                        callId,
                        operation: CRUD_OPERATION.UPDATE,
                    },
                } ),
            } );
    
            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                data: get( response, 'body.component.data.state', get( response, 'body.component.data', {} ) ),
            };

        } catch( error ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
            };
        }
    }

    // format error response
    _errorFormatter( errResponse ) {
        return errResponse; // some logic
    }


    // submits payment card activation request
    async submitActivatePaymentInfoRequest( { url, data } ) {
        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data
            } );
    
            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                data: get( response, 'body.component.data.state', get( response, 'body.component.data', {} ) )
            };

        } catch( error ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
            };
        }
    }

    // submits payment card activation request
    async submitToggleHsaFsaPaymentInfoRequest( { url, data } ) {
        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data
            } );
    
            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                data: get( response, 'body.component.data.state', get( response, 'body.component.data', {} ) )
            };

        } catch( error ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
            };
        }
    }
}
